/**
 * Ordena a listagem inteira: primeiro todos os produtos disponíveis, depois
 * todos os esgotados, atravessando todas as páginas da paginação.
 *
 * O Liquid já ordena dentro de cada página (dois passes) e marca cada item com
 * data-available. Este script substitui a paginação numerada por um botão
 * "Carregar mais" em duas fases: busca as páginas de `filter.v.availability=1`
 * e, quando elas acabam, as de `filter.v.availability=0`. A separação real é
 * feita por data-available em cada item (ver fetchIntoQueue), então funciona
 * com ou sem o filtro de disponibilidade habilitado no app Search & Discovery.
 * Com o filtro habilitado fica mais eficiente (menos páginas por fase).
 *
 * Serve a sections/collection.liquid e sections/search.liquid: o grid alvo é
 * qualquer elemento com data-sold-out-last="true" dentro de [data-products-grid].
 */
(function () {
  const AVAIL_IN_STOCK = '1';
  const AVAIL_SOLD_OUT = '0';

  const CONTAINER = '[data-products-grid]';
  const GRID = '[data-sold-out-last="true"]';
  const ITEM = '[data-product-index]';
  // Itens que não são produto (artigos/páginas na busca): não têm disponibilidade.
  const NON_PRODUCT = '[data-grid-item]:not([data-product-index])';
  const FEATURED = '[data-featured-position]';

  const state = {
    phase: AVAIL_IN_STOCK, // fase atual de carregamento
    nextUrl: null, // próxima url a buscar dentro da fase
    queue: [], // produtos buscados e ainda não inseridos (em ordem)
    singlePhase: false, // true = não reordena, usa a paginação do servidor
    featured: [], // blocos de imagem destacada pendentes: {node, position}
    slot: 0, // posições ocupadas no grid (produtos + imagens destacadas)
    totalLoaded: 0,
    totalProducts: 0,
    pageSize: 24,
    booting: false,
    loading: false,
  };

  let container = null; // [data-products-grid]
  let button = null; // .js-load-more
  let buttonWrapper = null; // [data-load-more-button]
  let currentGrid = null; // <ul>/<div> atual (detecta re-render dos filtros)

  function getContainer() {
    return document.querySelector(CONTAINER);
  }

  function getGrid() {
    const root = container || document;
    return root.querySelector(GRID);
  }

  // Lê o próximo link da paginação renderizado pelo snippet load-more
  // (input[data-next-url]) dentro de um documento qualquer.
  function readNextUrl(root) {
    const input = root.querySelector(CONTAINER + ' input[data-next-url]');
    const value = input ? input.value.trim() : '';
    return value === '' ? null : value;
  }

  // Monta url da página atual (preservando filtros e ordenação do usuário) com
  // o filtro de disponibilidade forçado e a página opcional.
  function buildUrl(availability, page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    if (page) url.searchParams.set('page', page);
    url.searchParams.set('filter.v.availability', availability);
    return url.pathname + '?' + url.searchParams.toString();
  }

  async function fetchDoc(url) {
    const response = await fetch(url);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function hasMore() {
    return state.queue.length > 0 || state.nextUrl != null;
  }

  // Avança a fase: se acabaram os disponíveis, passa para os esgotados.
  function advancePhase(nextWithinPhase) {
    if (nextWithinPhase) {
      state.nextUrl = nextWithinPhase;
    } else if (state.phase === AVAIL_IN_STOCK) {
      state.phase = AVAIL_SOLD_OUT;
      state.nextUrl = buildUrl(AVAIL_SOLD_OUT);
    } else {
      state.nextUrl = null;
    }
  }

  // Busca a próxima url e empilha na fila apenas os itens da fase atual
  // (disponíveis ou esgotados, via data-available). Filtrar por data-available
  // garante zero duplicação e funciona mesmo sem o filtro filter.v.availability
  // ativo na loja. Avança a fase/url para a próxima busca.
  async function fetchIntoQueue() {
    const doc = await fetchDoc(state.nextUrl);
    const grid = doc.querySelector(GRID) || doc.querySelector(CONTAINER);
    if (!grid) {
      state.nextUrl = null;
      return;
    }

    // Busca com artigos/páginas no meio: não há como ordenar por estoque sem
    // perdê-los, então desiste da reordenação e segue com a paginação normal.
    if (!state.singlePhase && grid.querySelector(NON_PRODUCT)) {
      state.singlePhase = true;
      state.phase = AVAIL_IN_STOCK;
    }

    const wantAvailable = state.phase === AVAIL_IN_STOCK;

    grid.querySelectorAll(ITEM).forEach((product) => {
      if (!state.singlePhase) {
        const isAvailable = product.dataset.available !== 'false';
        if (isAvailable !== wantAvailable) return; // ignora produtos de outra fase
      }
      state.queue.push(product.cloneNode(true));
    });

    const nextUrl = readNextUrl(doc);
    // passthrough: usa a paginação do servidor (com o filtro do usuário)
    if (state.singlePhase) state.nextUrl = nextUrl;
    else advancePhase(nextUrl);
  }

  // Garante que a fila tenha itens, buscando páginas (e trocando de fase) até
  // aparecer algum produto ou não haver mais nada.
  async function ensureQueue() {
    while (state.queue.length === 0 && state.nextUrl) {
      await fetchIntoQueue();
    }
  }

  // Reinsere os blocos de imagem destacada nas posições configuradas, contando
  // o mesmo slot que o Liquid conta (imagem ocupa uma célula do grid).
  function placeFeatured(grid) {
    while (state.featured.length && state.featured[0].position <= state.slot + 1) {
      grid.appendChild(state.featured.shift().node);
      state.slot += 1;
    }
  }

  // Insere até `limit` produtos no fim da grade, atravessando a fronteira
  // disponível->esgotado sem deixar lote órfão.
  async function appendBatch(limit) {
    const grid = getGrid();
    if (!grid) return;
    let added = 0;
    while (added < limit && state.totalLoaded < state.totalProducts) {
      if (state.queue.length === 0) await ensureQueue();
      if (state.queue.length === 0) break;
      placeFeatured(grid);
      const item = state.queue.shift();
      state.totalLoaded += 1;
      state.slot += 1;
      item.dataset.productIndex = String(state.totalLoaded);
      grid.appendChild(item);
      added += 1;
    }
    // Os cards são custom elements (<grid-item>) e o tema observa o DOM para as
    // animações AOS: nada mais precisa ser re-inicializado após o append.
  }

  function reindex(items) {
    items.forEach((item, i) => {
      item.dataset.productIndex = String(i + 1);
    });
  }

  function updateUI() {
    const finished = !hasMore() || state.totalLoaded >= state.totalProducts;
    if (buttonWrapper) buttonWrapper.classList.toggle('hidden', finished);
  }

  async function loadNext() {
    if (state.loading || state.booting || !hasMore()) return;
    state.loading = true;
    if (button) {
      button.setAttribute('disabled', '');
      button.classList.add('is-loading');
    }
    try {
      await appendBatch(state.pageSize);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      state.loading = false;
      if (button) {
        button.removeAttribute('disabled');
        button.classList.remove('is-loading');
      }
      updateUI();
    }
  }

  // Prepara a listagem sem jogar fora o conteúdo do servidor quando possível,
  // evitando o "pisca" (produtos somem e demoram a voltar).
  //
  // - Página única: o servidor já ordenou tudo (dois passes no Liquid).
  // - 1a página sem esgotados: idêntica à página 1 filtrada por disponível,
  //   então adota-se direto e continua na página 2 filtrada. Sem fetch.
  // - 1a página com esgotado: busca a página 1 filtrada e só troca depois de
  //   pronta (o conteúdo do servidor fica visível até o swap).
  async function bootstrap() {
    const grid = getGrid();
    if (!grid || state.booting) return;
    state.booting = true;

    state.totalProducts = parseInt(grid.dataset.totalProducts, 10) || 0;
    state.pageSize = parseInt(grid.dataset.pageSize, 10) || state.pageSize;
    state.phase = AVAIL_IN_STOCK;
    state.queue = [];
    state.featured = [];
    state.slot = 0;

    // Se o usuário filtrou disponibilidade nos filtros, a URL já traz
    // filter.v.availability. Nesse caso NÃO reordenamos: respeitamos a seleção
    // do usuário e usamos a paginação normal do servidor. Idem quando a busca
    // devolve artigos/páginas junto dos produtos.
    const params = new URLSearchParams(window.location.search);
    state.singlePhase =
      params.has('filter.v.availability') || grid.querySelector(NON_PRODUCT) != null;
    // Só a página 1 do servidor pode ser adotada como início da fase
    // "disponíveis"; numa url com ?page=N o conteúdo não corresponde.
    const onFirstPage = (parseInt(params.get('page'), 10) || 1) === 1;

    const serverItems = Array.from(grid.querySelectorAll(ITEM));
    const serverFeatured = Array.from(grid.querySelectorAll(FEATURED));
    const serverNextUrl = readNextUrl(document);
    const morePages = serverNextUrl != null;
    const soldOutOnPage = serverItems.some((item) => item.dataset.available === 'false');

    try {
      if (state.singlePhase || !morePages || (!soldOutOnPage && onFirstPage)) {
        // adota o conteúdo do servidor (sem fetch)
        reindex(serverItems);
        state.totalLoaded = serverItems.length;
        state.slot = serverItems.length + serverFeatured.length;
        if (!morePages) {
          state.nextUrl = null;
        } else if (state.singlePhase) {
          state.nextUrl = serverNextUrl; // paginação do servidor (com filtro do usuário)
        } else {
          state.nextUrl = buildUrl(AVAIL_IN_STOCK, 2);
        }
        return;
      }

      // fallback: 1a página tem esgotado -> busca filtrada e troca quando pronta
      state.nextUrl = buildUrl(AVAIL_IN_STOCK);
      await ensureQueue(); // carrega o 1o lote antes de remover o servidor
      state.featured = serverFeatured
        .map((node) => ({node, position: parseInt(node.dataset.featuredPosition, 10) || 1}))
        .sort((a, b) => a.position - b.position);
      state.featured.forEach((entry) => entry.node.remove());
      serverItems.forEach((item) => item.remove());
      state.totalLoaded = 0;
      state.slot = 0;
      await appendBatch(state.pageSize);
    } catch (error) {
      console.error('Error bootstrapping product grid:', error);
    } finally {
      state.booting = false;
      updateUI();
    }
  }

  function setup() {
    container = getContainer();
    if (!container) return;
    currentGrid = getGrid();
    if (!currentGrid) return;

    buttonWrapper = container.querySelector('[data-load-more-button]');
    button = container.querySelector('.js-load-more');

    if (button && button.dataset.soldOutLastBound !== 'true') {
      button.dataset.soldOutLastBound = 'true';
      button.addEventListener('click', loadNext);
    }

    bootstrap();
  }

  setup();

  // collection-filters-form.js troca o innerHTML de [data-products-grid] ao
  // filtrar/ordenar (um novo grid). Observamos essa troca para re-inicializar.
  // Comparamos a identidade do grid para não reagir às nossas próprias
  // mutações, que não trocam o grid (evita loop).
  const observed = getContainer();
  if (observed) {
    const observer = new MutationObserver(() => {
      const grid = getGrid();
      if (grid && grid !== currentGrid) setup();
    });
    observer.observe(observed, {childList: true, subtree: true});
  }
})();
